/**
 * D-037 — avaliação neonatal a partir do que já está registrado: junta a adequação do tratamento
 * materno (D-004), os títulos da mãe e do RN coletados no mesmo dia e a avaliação clínica do RN,
 * e pede ao fluxograma (D-002) a sugestão. O médico confirma; se um dado mudar depois da
 * confirmação, a conduta fica "a revisar".
 */
import { formatarDataBR } from '../datas/gestacao';
import type { ClassificacaoNeonatal, ResultadoAvaliacao } from '../validacao/esquemas-maternidade';
import { avaliarAdequacao, type AvaliacaoAdequacao } from './adequacao';
import { sugerirCondutaRn, type DesfechoRn, type SugestaoRn } from './conduta-rn';
import type { DoseInformada } from './doses';
import type {
  EstagioSifilis,
  RegrasAdequacao,
  RegrasCondutaRn,
  RegrasTratamentoAdulto,
} from './regras';
import type { ResultadoTitulo } from './titulos';

export const CLASSIFICACAO_DO_DESFECHO: Record<DesfechoRn, ClassificacaoNeonatal> = {
  crianca_exposta: 'crianca_exposta',
  avaliacao_storch: 'avaliacao_storch',
  benzatina_dose_unica: 'tratamento_benzatina_dose_unica',
  sc_sem_neurossifilis: 'sifilis_congenita_sem_neurossifilis',
  sc_com_neurossifilis: 'sifilis_congenita_com_neurossifilis',
};

export const ROTULO_CLASSIFICACAO_NEONATAL: Record<ClassificacaoNeonatal, string> = {
  crianca_exposta: 'Criança exposta à sífilis — sem tratamento imediato; seguimento',
  avaliacao_storch: 'Avaliação para STORCH',
  tratamento_benzatina_dose_unica: 'Penicilina benzatina, dose única',
  sifilis_congenita_sem_neurossifilis: 'Sífilis congênita sem neurossífilis',
  sifilis_congenita_com_neurossifilis: 'Sífilis congênita com neurossífilis',
};

/** Classificações que caracterizam caso de sífilis congênita (notificação obrigatória). */
export const CLASSIFICACOES_SIFILIS_CONGENITA: readonly ClassificacaoNeonatal[] = [
  'tratamento_benzatina_dose_unica',
  'sifilis_congenita_sem_neurossifilis',
  'sifilis_congenita_com_neurossifilis',
];

/** Teste não treponêmico (VDRL/RPR) com resultado. */
export interface TesteNaoTreponemico {
  tipo: string;
  data: string;
  resultado: string;
  titulo: string | null;
}

const comResultado = (t: TesteNaoTreponemico) =>
  ['vdrl', 'rpr'].includes(t.tipo) &&
  (t.resultado === 'nao_reagente' || (t.resultado === 'reagente' && Boolean(t.titulo)));

const comoTitulo = (t: TesteNaoTreponemico): ResultadoTitulo =>
  t.resultado === 'reagente' ? (t.titulo as string) : 'nao_reagente';

/**
 * Títulos da mãe e do RN "coletados ao mesmo tempo" = no mesmo dia (pergunta ao corpo clínico, §9).
 * Usa o par mais recente; havendo mais de um teste no dia, prefere o mesmo método (VDRL × VDRL).
 */
export function titulosPareados(
  daMae: TesteNaoTreponemico[],
  doRn: TesteNaoTreponemico[],
): { data: string; tituloMae: ResultadoTitulo; tituloRn: ResultadoTitulo } | null {
  const mae = daMae.filter(comResultado);
  const rn = doRn.filter(comResultado).sort((a, b) => b.data.localeCompare(a.data));
  for (const r of rn) {
    const mesmoDia = mae.filter((m) => m.data === r.data);
    const m = mesmoDia.find((x) => x.tipo === r.tipo) ?? mesmoDia[0];
    if (m) return { data: r.data, tituloMae: comoTitulo(m), tituloRn: comoTitulo(r) };
  }
  return null;
}

const normal = (r: ResultadoAvaliacao | null | undefined): boolean | null =>
  r === 'normal' ? true : r === 'alterado' ? false : null;

export interface EntradaNeonatal {
  dataParto: string;
  /** Tratamento de sífilis da mãe na gestação (null = não tratada). */
  tratamentoMae: {
    medicamento: string | null;
    estagio: EstagioSifilis;
    doses: DoseInformada[];
  } | null;
  /** Correção do médico à adequação calculada (D-004), com justificativa. */
  maeAdequadaInformada?: boolean | null;
  testesMae: TesteNaoTreponemico[];
  testesRn: TesteNaoTreponemico[];
  clinica: {
    exameFisico?: ResultadoAvaliacao | null;
    hemograma?: ResultadoAvaliacao | null;
    liquor?: ResultadoAvaliacao | null;
    rxOssosLongos?: ResultadoAvaliacao | null;
  };
}

export interface AvaliacaoNeonatal {
  adequacao: AvaliacaoAdequacao;
  /** Valor usado no fluxograma: o informado pelo médico, se houver; senão o calculado. */
  maeAdequada: boolean;
  titulos: ReturnType<typeof titulosPareados>;
  sugestao: SugestaoRn;
  classificacaoSugerida: ClassificacaoNeonatal | null;
  tituloRnMaior: boolean | null;
}

export function avaliarNeonatal(
  regras: {
    conduta: RegrasCondutaRn;
    adequacao: RegrasAdequacao;
    tratamento: RegrasTratamentoAdulto;
  },
  e: EntradaNeonatal,
): AvaliacaoNeonatal {
  const adequacao = avaliarAdequacao(
    { adequacao: regras.adequacao, tratamento: regras.tratamento },
    {
      medicamento: e.tratamentoMae?.medicamento ?? null,
      estagio: e.tratamentoMae?.estagio ?? 'ignorado',
      doses: e.tratamentoMae?.doses ?? [],
      dataParto: e.dataParto,
    },
  );
  const informada = e.maeAdequadaInformada ?? null;
  const maeAdequada = informada ?? adequacao.adequado;
  const titulos = titulosPareados(e.testesMae, e.testesRn);

  const sugestao = sugerirCondutaRn(regras.conduta, {
    maeAdequada,
    origemAdequacao:
      informada === null
        ? `${adequacao.resumo} (parto em ${formatarDataBR(e.dataParto)})`
        : undefined,
    tituloMae: titulos?.tituloMae ?? null,
    tituloRn: titulos?.tituloRn ?? null,
    exameFisicoNormal: normal(e.clinica.exameFisico),
    hemogramaNormal: normal(e.clinica.hemograma),
    liquorNormal: normal(e.clinica.liquor),
    rxOssosLongosNormal: normal(e.clinica.rxOssosLongos),
  });

  const passoTitulo = sugestao.passos.find((p) => p.pergunta.startsWith('Título do RN'));
  return {
    adequacao,
    maeAdequada,
    titulos,
    sugestao,
    classificacaoSugerida: sugestao.desfecho ? CLASSIFICACAO_DO_DESFECHO[sugestao.desfecho] : null,
    tituloRnMaior: passoTitulo ? passoTitulo.resposta.startsWith('sim') : null,
  };
}

/**
 * Conduta confirmada que precisa ser revista: a sugestão calculada hoje é diferente da que
 * existia quando o médico confirmou (chegou um exame, mudou um resultado…).
 */
export function condutaARevisar(registro: {
  confirmada: boolean;
  sugeridaNaConfirmacao: ClassificacaoNeonatal | null;
  sugeridaAgora: ClassificacaoNeonatal | null;
}): boolean {
  return registro.confirmada && registro.sugeridaNaConfirmacao !== registro.sugeridaAgora;
}
